/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import Component from '@glimmer/component';
import { inject as service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import CustomerModel from 'cio/models/customer';
import LogModel from 'cio/models/log';
import RavenService from 'cio/services/raven';
import { ActivityLogType } from 'cio/utils/activity-log-types';
import isDestroyed from 'cio/utils/is-destroyed';

interface ActivityLogRowPersonArgs {
  isAnonymous?: boolean;
  log: LogModel;
}

export default class ActivityLogRowPerson extends Component<ActivityLogRowPersonArgs> {
  @service raven!: RavenService;

  constructor(owner: unknown, args: ActivityLogRowPersonArgs) {
    super(owner, args);

    this.loadCustomerIfNecessary();
  }

  @tracked customer: CustomerModel | null = null;

  private async loadCustomerIfNecessary() {
    let { log } = this.args;

    if (isDestroyed(log) || log.belongsTo('customer').id() == null) {
      return;
    }

    try {
      // Customers are included in the logs API endpoint response. If the customer
      // isn't loaded into the store at this point, it means the customer is
      // deleted

      this.customer = log.belongsTo('customer').value() as CustomerModel | null;
    } catch (err) {
      // this error is thrown sometimes in tests:
      // "TypeError: Cannot set property id of [object Object] which has only a getter"
      //
      // It's this test: http://localhost:4200/tests?testId=94c1b71a . But it
      // fails only semi-randomly when run with others, as part of a full partition.
      //
      // TODO: this could be investigated more BUT:
      // It's also probably not worth it - let's ignore it and not load a
      // customer, call it a day

      // Let's also report it to Sentry - to catch it in case it ever happens in prod (even though it probably doesn't)
      this.raven.captureException(err);
    }
  }

  get hasCustomer() {
    return this.customer != null;
  }

  get isInvite() {
    let { attrs } = this.args.log;

    if (Array.isArray(attrs) || attrs == null) {
      return false;
    }

    return !this.args.log.customerId && attrs.recipient;
  }

  get isDataCampaignLog() {
    if (isDestroyed(this.args.log)) {
      return false;
    }

    if (this.args.log.type === ActivityLogType.WebhookEvent) {
      return true;
    }

    // If the customer_id is null on the log and the action is a webhook or
    // slack, then there is no person tied to the log (it's an action from a
    // data campaign) and we should display "N/A"
    return (
      this.args.log.belongsTo('customer').id() == null && ['slack', 'webhook'].includes(this.args.log.deliveryType)
    );
  }
}
